import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API_BASE = "https://api.vyapakprint.com/api";

const ADDON_CONFIG = [
  {
    key: "dieShape",
    title: "Die Shape",
    enabledField: "isDieShape",
    listField: "dieShapes",
    placeholder: "Die Shape Type",
  },
  {
    key: "goldFoil",
    title: "Gold Foil",
    enabledField: "isGoldFoil",
    listField: "goldFoils",
    placeholder: "Gold Foil Type",
  },
  {
    key: "goldColor",
    title: "Gold Color",
    enabledField: "isGoldColor",
    listField: "goldColors",
    placeholder: "Gold Color Type",
  },
  {
    key: "spotUV",
    title: "Spot UV",
    enabledField: "isSpotUV",
    listField: "spotUVOptions",
    placeholder: "Spot UV Type",
  },
  {
    key: "mattelColor",
    title: "Mattel Color",
    enabledField: "isMattelColor",
    listField: "mattelColors",
    placeholder: "Mattel Color Type",
  },
  {
    key: "binding",
    title: "Binding",
    enabledField: "isBinding",
    listField: "bindings",
    placeholder: "Binding Type",
  },
  {
    key: "size",
    title: "Size",
    enabledField: "isSize",
    listField: "sizes",
    placeholder: "Size Type",
  },
  {
    key: "paperType",
    title: "Paper Type",
    enabledField: "isPaperType",
    listField: "paperTypes",
    placeholder: "Paper Type Name",
  },
  {
    key: "flapOpening",
    title: "Flap Opening",
    enabledField: "isFlapOpening",
    listField: "flapOpenings",
    placeholder: "Flap Opening Name",
  },
  {
    key: "windowCutting",
    title: "Window Cutting",
    enabledField: "isWindowCutting",
    listField: "windowCuttings",
    placeholder: "Window Cutting Name",
  },
  {
    key: "printing",
    title: "Printing",
    enabledField: "isPrinting",
    listField: "printings",
    placeholder: "Printing Name",
  },
  {
    key: "laminationType",
    title: "Lamination Type",
    enabledField: "isLaminationType",
    listField: "laminationTypes",
    placeholder: "Lamination Type Name",
  },
  {
    key: "textureType",
    title: "Texture Type",
    enabledField: "isTextureType",
    listField: "textureTypes",
    placeholder: "Texture Type Name",
  },
];

const emptyOption = () => ({
  name: "",
  pricePerUnit: 0,
});

const createInitialAddons = () =>
  ADDON_CONFIG.reduce((acc, item) => {
    acc[item.key] = {
      enabled: false,
      options: [],
    };
    return acc;
  }, {});

const normalizeOptionList = (arr = []) => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((item) => {
      if (typeof item === "string") {
        return { name: item, pricePerUnit: 0 };
      }
      return {
        name: String(item?.name || "").trim(),
        pricePerUnit: Number(item?.pricePerUnit || item?.price || 0),
      };
    })
    .filter((item) => item.name);
};

const normalizePricesObject = (value) => {
  if (!value) return {};
  if (value instanceof Map) return Object.fromEntries(value);
  if (typeof value === "object") return value;
  return {};
};

const formatCurrency = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0";
  return num.toLocaleString("en-IN");
};

const cleanBasePrices = (pricesObj = {}) => {
  return Object.entries(pricesObj).reduce((acc, [key, value]) => {
    const trimmedKey = String(key || "").trim();
    const num = Number(value);

    if (trimmedKey && Number.isFinite(num) && num >= 0) {
      acc[trimmedKey] = num;
    }

    return acc;
  }, {});
};

const cleanOptionItems = (items = []) => {
  return (Array.isArray(items) ? items : [])
    .map((item) => ({
      name: String(item?.name || "").trim(),
      pricePerUnit: Number(item?.pricePerUnit || 0),
    }))
    .filter((item) => item.name);
};

const extractCategoryValue = (category) => {
  if (!category) return "";
  if (typeof category === "string") return category;
  return category?._id || "";
};

const AddonOptionsEditor = ({
  title,
  enabled,
  setEnabled,
  options,
  setOptions,
  placeholder = "Option name",
  readOnlyNames = false,
  allowAddRemove = true,
}) => {
  const addRow = () => {
    setOptions((prev) => [...(Array.isArray(prev) ? prev : []), emptyOption()]);
  };

  const updateRow = (index, field, value) => {
    setOptions((prev) =>
      (Array.isArray(prev) ? prev : []).map((item, i) =>
        i === index
          ? {
              ...item,
              [field]:
                field === "pricePerUnit"
                  ? Number(value === "" ? 0 : value)
                  : value,
            }
          : item,
      ),
    );
  };

  const removeRow = (index) => {
    setOptions((prev) =>
      (Array.isArray(prev) ? prev : []).filter((_, i) => i !== index),
    );
  };

  return (
    <div className="border rounded-xl p-4 bg-gray-50">
      <div className="flex items-center justify-between gap-3 mb-3">
        <label className="flex items-center gap-2 font-medium">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => {
              const checked = e.target.checked;
              setEnabled(checked);
              if (!checked) {
                setOptions([]);
              } else if (!options?.length && allowAddRemove) {
                setOptions([emptyOption()]);
              }
            }}
          />
          {title}
        </label>

        {enabled && allowAddRemove && (
          <button
            type="button"
            onClick={addRow}
            className="px-3 py-1.5 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
          >
            Add Option
          </button>
        )}
      </div>

      {enabled && (
        <div className="space-y-3">
          {(options || []).map((item, index) => (
            <div
              key={`${title}-${index}`}
              className="grid grid-cols-1 md:grid-cols-[1fr_180px_100px] gap-3"
            >
              <input
                type="text"
                placeholder={placeholder}
                className={`w-full p-2 border rounded-lg ${
                  readOnlyNames ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                value={item?.name || ""}
                readOnly={readOnlyNames}
                onChange={(e) => updateRow(index, "name", e.target.value)}
              />

              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Price Per Unit"
                className="w-full p-2 border rounded-lg"
                value={item?.pricePerUnit ?? 0}
                onChange={(e) =>
                  updateRow(index, "pricePerUnit", e.target.value)
                }
              />

              {allowAddRemove ? (
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  className="px-3 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
                >
                  Remove
                </button>
              ) : (
                <div className="px-3 py-2 rounded-lg bg-gray-200 text-gray-600 text-center">
                  Fixed
                </div>
              )}
            </div>
          ))}

          {!options?.length && (
            <p className="text-sm text-gray-500">No options added yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

const BusinessProducts = () => {
  const [businessProducts, setBusinessProducts] = useState([]);
  const [businessProductName, setBusinessProductName] = useState("");
  const [businessProductPrice, setBusinessProductPrice] = useState({});
  const [businessProductShippingTime, setBusinessProductShippingTime] =
    useState("");
  const [businessProductImage, setBusinessProductImage] = useState(null);
  const [minimumQuantity, setMinimumQuantity] = useState(1);
  const [userTypes, setUserTypes] = useState([]);
  const [businessIsEditing, setBusinessIsEditing] = useState(false);
  const [businessEditProductId, setBusinessEditProductId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingUserTypes, setLoadingUserTypes] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [businessProductDescription, setBusinessProductDescription] =
    useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [addons, setAddons] = useState(createInitialAddons());
  const [allDieShapes, setAllDieShapes] = useState([]);

  const fetchUserTypes = async () => {
    setLoadingUserTypes(true);
    try {
      const response = await axios.get(`${API_BASE}/userType/getUserTypes`);
      setUserTypes(response.data || []);
    } catch (error) {
      console.error("Error fetching user types:", error);
    } finally {
      setLoadingUserTypes(false);
    }
  };

  const fetchBusinessProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await axios.get(
        `${API_BASE}/businessProducts/getAllBusinessProducts`,
      );
      setBusinessProducts(response.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE}/category-options`);
      setCategories(res.data?.options || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchDieShapes = async () => {
    try {
      const res = await axios.get(`${API_BASE}/dieshapes`);
      const list = Array.isArray(res.data) ? res.data : [];
      setAllDieShapes(
        list
          .map((item) => ({
            name: String(item?.name || "").trim(),
            pricePerUnit: 0,
          }))
          .filter((item) => item.name),
      );
    } catch (error) {
      console.error("Error fetching die shapes:", error);
    }
  };

  useEffect(() => {
    fetchUserTypes();
    fetchBusinessProducts();
    fetchCategories();
    fetchDieShapes();
  }, []);

  const handlePriceChange = (userTypeId, value) => {
    setBusinessProductPrice((prev) => ({
      ...prev,
      [userTypeId]: value,
    }));
  };

  const setAddonEnabled = (key, enabled) => {
    setAddons((prev) => {
      if (key === "dieShape") {
        return {
          ...prev,
          [key]: {
            ...prev[key],
            enabled,
            options: enabled
              ? allDieShapes.map((shape) => {
                  const existing = (prev[key]?.options || []).find(
                    (opt) => opt.name === shape.name,
                  );
                  return {
                    name: shape.name,
                    pricePerUnit: Number(existing?.pricePerUnit || 0),
                  };
                })
              : [],
          },
        };
      }

      return {
        ...prev,
        [key]: {
          ...prev[key],
          enabled,
          options: enabled
            ? prev[key]?.options?.length
              ? prev[key].options
              : [emptyOption()]
            : [],
        },
      };
    });
  };

  const setAddonOptions = (key, updater) => {
    setAddons((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        options:
          typeof updater === "function"
            ? updater(prev[key]?.options || [])
            : updater,
      },
    }));
  };

  const filteredProducts = useMemo(() => {
    return businessProducts.filter(({ name = "" }) =>
      String(name).toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [businessProducts, searchTerm]);

  const buildFormData = () => {
    const formData = new FormData();

    formData.append("name", businessProductName.trim());
    formData.append("description", businessProductDescription.trim());
    formData.append(
      "prices",
      JSON.stringify(cleanBasePrices(businessProductPrice)),
    );
    formData.append("shippingTime", businessProductShippingTime.trim());
    formData.append("minimumQuantity", String(Number(minimumQuantity || 1)));
    formData.append("videoUrl", videoUrl.trim() || "");
    formData.append("category", selectedCategory);

    if (businessProductImage) {
      formData.append("image", businessProductImage);
    }

    ADDON_CONFIG.forEach((addon) => {
      const addonState = addons[addon.key] || { enabled: false, options: [] };
      const cleanedOptions = cleanOptionItems(addonState.options);

      formData.append(addon.enabledField, String(Boolean(addonState.enabled)));
      formData.append(addon.listField, JSON.stringify(cleanedOptions));
    });

    return formData;
  };

  const validateForm = ({ isEdit = false } = {}) => {
    if (!selectedCategory) {
      alert("Please select a category.");
      return false;
    }

    if (!businessProductName.trim()) {
      alert("Please enter product name.");
      return false;
    }

    if (!businessProductDescription.trim()) {
      alert("Please enter product description.");
      return false;
    }

    const cleanedPrices = cleanBasePrices(businessProductPrice);
    if (!Object.keys(cleanedPrices).length) {
      alert("Please enter at least one base price.");
      return false;
    }

    if (!businessProductShippingTime.trim()) {
      alert("Please enter shipping time.");
      return false;
    }

    if (!minimumQuantity || Number(minimumQuantity) < 1) {
      alert("Minimum quantity must be at least 1.");
      return false;
    }

    if (!isEdit && !businessProductImage) {
      alert("Please upload product image.");
      return false;
    }

    for (const addon of ADDON_CONFIG) {
      const addonState = addons[addon.key];

      if (addonState?.enabled) {
        const validItems = cleanOptionItems(addonState.options);
        if (!validItems.length) {
          alert(`Please add at least one option in ${addon.title}.`);
          return false;
        }
      }
    }

    return true;
  };

  const addBusinessProduct = async () => {
    if (!validateForm({ isEdit: false })) return;

    setSavingProduct(true);
    try {
      const response = await axios.post(
        `${API_BASE}/businessProducts/createBusinessProduct`,
        buildFormData(),
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      setBusinessProducts((prev) => [...prev, response.data]);
      alert("Product added successfully!");
      resetForm();
    } catch (error) {
      console.error("Error adding product:", error);
      alert(
        error?.response?.data?.message ||
          "Error adding product. Please try again.",
      );
    } finally {
      setSavingProduct(false);
    }
  };

  const editBusinessProduct = (productId) => {
    const product = businessProducts.find((p) => p._id === productId);
    if (!product) return;

    setBusinessProductName(product.name || "");
    setBusinessProductPrice(normalizePricesObject(product.prices));
    setBusinessProductShippingTime(product.shippingTime || "");
    setBusinessProductImage(null);
    setMinimumQuantity(Number(product.minimumQuantity || 1));
    setBusinessProductDescription(product.description || "");
    setVideoUrl(product.videoUrl || "");
    setSelectedCategory(extractCategoryValue(product.category));

    const nextAddons = createInitialAddons();

    ADDON_CONFIG.forEach((addon) => {
      const existingOptions = normalizeOptionList(product?.[addon.listField]);

      if (addon.key === "dieShape") {
        const mergedDieOptions = allDieShapes.map((shape) => {
          const matched = existingOptions.find(
            (opt) => opt.name === shape.name,
          );
          return {
            name: shape.name,
            pricePerUnit: Number(matched?.pricePerUnit || 0),
          };
        });

        nextAddons[addon.key] = {
          enabled: Boolean(product?.[addon.enabledField]),
          options: Boolean(product?.[addon.enabledField])
            ? mergedDieOptions
            : [],
        };
      } else {
        nextAddons[addon.key] = {
          enabled: Boolean(product?.[addon.enabledField]),
          options: existingOptions,
        };
      }
    });

    setAddons(nextAddons);
    setBusinessIsEditing(true);
    setBusinessEditProductId(product._id);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateBusinessProduct = async () => {
  if (!validateForm({ isEdit: true })) return;
  if (!window.confirm("Are you sure you want to update this product?")) {
    return;
  }

  setSavingProduct(true);
  try {
    const response = await axios.put(
      `${API_BASE}/businessProducts/updateBusinessProduct/${businessEditProductId}`,
      buildFormData(),
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );

    setBusinessProducts((prev) =>
      prev.map((product) =>
        product._id === businessEditProductId ? response.data : product,
      ),
    );

    alert("Product updated successfully!");
    // do not reset form
    // resetForm();
  } catch (error) {
    console.error("Error updating product:", error);
    alert(
      error?.response?.data?.message ||
        "Error updating product. Please try again.",
    );
  } finally {
    setSavingProduct(false);
  }
};

  const deleteBusinessProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE}/businessProducts/deleteBusinessProduct/${productId}`,
      );
      setBusinessProducts((prev) => prev.filter((p) => p._id !== productId));
      alert("Product deleted successfully!");
    } catch (error) {
      console.error("Error deleting product:", error);
      alert(
        error?.response?.data?.message ||
          "Error deleting product. Please try again.",
      );
    }
  };

  const resetForm = () => {
    setBusinessProductName("");
    setBusinessProductPrice({});
    setBusinessProductShippingTime("");
    setBusinessProductImage(null);
    setMinimumQuantity(1);
    setBusinessProductDescription("");
    setVideoUrl("");
    setSelectedCategory("");
    setAddons(createInitialAddons());
    setBusinessIsEditing(false);
    setBusinessEditProductId(null);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="bg-white shadow-md rounded-lg p-4 md:p-6 mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          {businessIsEditing ? "Edit Product" : "Add New Product"}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block font-medium mb-1">Select Category</label>
            <select
              className="w-full p-2 border rounded-lg"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">-- Select --</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>

            <Link
              to="/businessCategory"
              className="inline-block mt-2 text-blue-600 underline hover:text-blue-800 transition"
            >
              Click here to create a new business category
            </Link>
          </div>

          <input
            type="text"
            placeholder="Product Name"
            className="w-full p-2 border rounded-lg"
            value={businessProductName}
            onChange={(e) => setBusinessProductName(e.target.value)}
          />

          <textarea
            placeholder="Product Description"
            className="w-full p-2 border rounded-lg"
            rows={4}
            value={businessProductDescription}
            onChange={(e) => setBusinessProductDescription(e.target.value)}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {userTypes.map((userType) => (
              <div key={userType._id}>
                <label className="block mb-1 font-medium">
                  {userType.type} Base Price
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={`Price for ${userType.type}`}
                  className="w-full p-2 border rounded-lg"
                  value={businessProductPrice[userType._id] || ""}
                  onChange={(e) =>
                    handlePriceChange(userType._id, e.target.value)
                  }
                />
              </div>
            ))}
          </div>

          <input
            type="text"
            placeholder="Shipping Time"
            className="w-full p-2 border rounded-lg"
            value={businessProductShippingTime}
            onChange={(e) => setBusinessProductShippingTime(e.target.value)}
          />

          <input
            type="file"
            accept="image/*"
            className="w-full p-2 border rounded-lg"
            onChange={(e) =>
              setBusinessProductImage(e.target.files?.[0] || null)
            }
          />

          <div>
            <label className="block mb-1 font-medium">Minimum Quantity</label>
            <input
              type="number"
              min="1"
              className="w-full p-2 border rounded-lg"
              value={minimumQuantity}
              onChange={(e) => setMinimumQuantity(Number(e.target.value || 1))}
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">
              Video URL (optional)
            </label>
            <input
              type="text"
              placeholder="YouTube link"
              className="w-full p-2 border rounded-lg"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">
              All Add-ons with Types / Options
            </h3>

            <div className="grid grid-cols-1 gap-4">
              {ADDON_CONFIG.map((addon) => {
                const isDieShape = addon.key === "dieShape";

                return (
                  <AddonOptionsEditor
                    key={addon.key}
                    title={addon.title}
                    enabled={addons?.[addon.key]?.enabled || false}
                    setEnabled={(enabled) =>
                      setAddonEnabled(addon.key, enabled)
                    }
                    options={addons?.[addon.key]?.options || []}
                    setOptions={(updater) =>
                      setAddonOptions(addon.key, updater)
                    }
                    placeholder={addon.placeholder}
                    readOnlyNames={isDieShape}
                    allowAddRemove={!isDieShape}
                  />
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            className={`${
              savingProduct
                ? "bg-blue-300 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600"
            } text-white px-4 py-2 rounded-lg transition`}
            disabled={savingProduct}
            onClick={
              businessIsEditing ? updateBusinessProduct : addBusinessProduct
            }
          >
            {savingProduct
              ? businessIsEditing
                ? "Updating..."
                : "Adding..."
              : businessIsEditing
                ? "Update Product"
                : "Add Product"}
          </button>

          {businessIsEditing && (
            <button
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              onClick={resetForm}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg p-4 md:p-6">
        <h2 className="text-2xl font-semibold mb-4">Product List</h2>

        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full mb-4 px-4 py-2 border rounded-lg focus:outline-none focus:ring"
        />

        {(loadingProducts || loadingUserTypes) && (
          <p className="text-center py-6">Loading...</p>
        )}

        {!loadingProducts && filteredProducts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-3">Image</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Description</th>
                  {userTypes.map((u) => (
                    <th key={u._id} className="p-3 whitespace-nowrap">
                      Base Price ({u.type})
                    </th>
                  ))}
                  <th className="p-3">Shipping</th>
                  <th className="p-3">Min Qty</th>
                  <th className="p-3">Add-ons</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredProducts.map((product) => {
                  const productPrices = normalizePricesObject(product.prices);

                  const addonSummary = ADDON_CONFIG.map((addon) => {
                    const enabled = Boolean(product?.[addon.enabledField]);
                    const options = normalizeOptionList(
                      product?.[addon.listField],
                    );

                    if (!enabled) return null;

                    if (options.length) {
                      return `${addon.title} (${options.length})`;
                    }

                    return addon.title;
                  })
                    .filter(Boolean)
                    .join(", ");

                  return (
                    <tr key={product._id} className="border-b last:border-b-0">
                      <td className="p-3">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-14 h-14 object-cover rounded"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </td>

                      <td className="p-3 font-medium">{product.name}</td>
                      <td className="p-3">{product.category?.name || "—"}</td>
                      <td className="p-3 max-w-xs truncate">
                        {product.description || "—"}
                      </td>

                      {userTypes.map((u) => (
                        <td key={u._id} className="p-3 whitespace-nowrap">
                          ₹
                          {productPrices?.[u._id] !== undefined
                            ? formatCurrency(productPrices?.[u._id])
                            : "—"}
                        </td>
                      ))}

                      <td className="p-3">{product.shippingTime || "—"}</td>
                      <td className="p-3">{product.minimumQuantity || "—"}</td>
                      <td className="p-3 max-w-sm">{addonSummary || "—"}</td>

                      <td className="p-3 space-x-2 whitespace-nowrap">
                        <button
                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded"
                          onClick={() => editBusinessProduct(product._id)}
                        >
                          Edit
                        </button>
                        <button
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded"
                          onClick={() => deleteBusinessProduct(product._id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loadingProducts && filteredProducts.length === 0 && (
          <p className="text-center text-gray-500 py-6">No products found.</p>
        )}
      </div>
    </div>
  );
};

export default BusinessProducts;
